import axios from 'axios';
import { localBackendUrl } from './localBackend';
import { API } from '../config/constants';

export const AUTH_TOKEN_KEY = 'lvb_token';
export const AUTH_SESSION_TYPE_KEY = 'lvb_session_type';
export const OFFLINE_AUTH_TOKEN_KEY = 'lvb_offline_token';
export const SESSION_TYPE_CLOUD = 'cloud';
export const SESSION_TYPE_OFFLINE = 'offline';
export const AUTH_INVALID_EVENT = 'lvb:auth-invalid';
export const API_SERVER_ERROR_EVENT = 'lvb:server-error';

const REQUEST_REFERENCE_LENGTH = 12;
let desktopRendererSession = null;
let activeSessionRevision = 0;
let activeTransitionOwner = 0;
let desktopSessionQueue = Promise.resolve();
const sessionRevisionListeners = new Set();

const advanceSessionRevision = () => {
  activeSessionRevision += 1;
  sessionRevisionListeners.forEach((listener) => listener());
};

const replaceDesktopRendererSession = (session) => {
  desktopRendererSession = session;
  advanceSessionRevision();
};

// This queue orders renderer adoption only. Native owns verification and
// cancellation; a screen can read a verified session but cannot establish one.
const queueDesktopSession = (operation) => {
  const result = desktopSessionQueue.then(operation);
  desktopSessionQueue = result.catch(() => {});
  return result;
};

export const isSessionTransitionCurrent = (owner) => owner === activeTransitionOwner;

export const beginSessionTransition = () => {
  activeTransitionOwner += 1;
  advanceSessionRevision();
  return activeTransitionOwner;
};

const isDesktopRenderer = () => (
  typeof window !== 'undefined' && Boolean(window.lvbDesktop)
);

const clearLegacyDesktopSessionKeys = () => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(OFFLINE_AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_SESSION_TYPE_KEY);
};

const isValidDesktopSession = (session) => (
  session
  && [SESSION_TYPE_CLOUD, SESSION_TYPE_OFFLINE].includes(session.sessionType)
  && typeof session.token === 'string'
  && session.token.length >= 16
  && session.token.length <= 8192
);

const isApiRequest = (url = '') => {
  if (typeof url !== 'string') return false;
  const baseUrl = localBackendUrl();
  const localApi = baseUrl ? `${baseUrl}/api` : null;
  return (
    matchesApiBase(url, API)
    || matchesApiBase(url, '/api')
    || Boolean(localApi && matchesApiBase(url, localApi))
  );
};

const matchesApiBase = (url, base) => (
  url === base || ['/', '?', '#'].some((separator) => url.startsWith(`${base}${separator}`))
);

const isLoginRequest = (url = '') => (
  typeof url === 'string' && url.includes('/auth/login')
);

const isLocalDesktopApiRequest = (url = '') => {
  if (typeof url !== 'string') return false;
  const baseUrl = localBackendUrl();
  return Boolean(baseUrl && matchesApiBase(url, `${baseUrl}/api`));
};

const getStoredSessionType = () => {
  if (isDesktopRenderer()) {
    return desktopRendererSession?.sessionType || SESSION_TYPE_CLOUD;
  }
  return localStorage.getItem(AUTH_SESSION_TYPE_KEY) || SESSION_TYPE_CLOUD;
};

const clearNativeSession = async (bridge) => {
  if (typeof bridge.clearSession !== 'function') {
    return { cleared: false, reason: 'bridge_unavailable' };
  }
  try {
    return await bridge.clearSession();
  } catch {
    return { cleared: false, reason: 'bridge_failed' };
  }
};

const storeSession = async (sessionType, token, user, owner) => {
  if (!isSessionTransitionCurrent(owner)) return false;
  if (isDesktopRenderer()) {
    clearLegacyDesktopSessionKeys();
    const bridge = window.lvbDesktop;
    return queueDesktopSession(async () => {
      if (!isSessionTransitionCurrent(owner)) return false;
      if (typeof bridge.getActiveSession !== 'function') {
        throw new Error('Desktop session protection is unavailable.');
      }
      const session = await bridge.getActiveSession();
      if (!isSessionTransitionCurrent(owner)) return false;
      if (!isValidDesktopSession(session) || session.token !== token
          || session.sessionType !== sessionType) {
        throw new Error('Desktop session protection rejected the session.');
      }
      replaceDesktopRendererSession(session);
      return true;
    });
  }
  if (sessionType === SESSION_TYPE_CLOUD) {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    localStorage.removeItem(OFFLINE_AUTH_TOKEN_KEY);
  } else {
    localStorage.setItem(OFFLINE_AUTH_TOKEN_KEY, token);
  }
  localStorage.setItem(AUTH_SESSION_TYPE_KEY, sessionType);
  advanceSessionRevision();
  return true;
};

export const storeCloudSession = (token, user = null, owner = beginSessionTransition()) => (
  storeSession(SESSION_TYPE_CLOUD, token, user, owner)
);

export const storeOfflineSession = (token, user = null, owner = beginSessionTransition()) => (
  storeSession(SESSION_TYPE_OFFLINE, token, user, owner)
);

export const clearStoredSession = (owner = beginSessionTransition()) => {
  if (!isSessionTransitionCurrent(owner)) {
    return Promise.resolve({ cleared: false, reason: 'superseded' });
  }
  replaceDesktopRendererSession(null);
  clearLegacyDesktopSessionKeys();
  if (isDesktopRenderer()) {
    const bridge = window.lvbDesktop;
    // Invalidate native pending verification immediately, not behind a slow
    // session read. IPC invocation order preserves logout then newer login.
    return clearNativeSession(bridge);
  }
  return Promise.resolve({ cleared: true });
};

export const hydrateStoredSession = async (owner = beginSessionTransition()) => {
  if (!isDesktopRenderer() || !isSessionTransitionCurrent(owner)) return null;
  clearLegacyDesktopSessionKeys();
  replaceDesktopRendererSession(null);
  const bridge = window.lvbDesktop;
  return queueDesktopSession(async () => {
    if (!isSessionTransitionCurrent(owner) || typeof bridge.getActiveSession !== 'function') return null;
    try {
      const session = await bridge.getActiveSession();
      if (!isSessionTransitionCurrent(owner) || !isValidDesktopSession(session)) return null;
      replaceDesktopRendererSession(session);
      return { ...session };
    } catch {
      return null;
    }
  });
};

export const getActiveSessionType = () => getStoredSessionType();

// Monotonic and deliberately non-secret. Long-running desktop work captures the
// current value and stops if login, logout, invalidation, or hydration replaces
// the session while that work is in flight.
export const getActiveSessionRevision = () => activeSessionRevision;

// React consumers pair this non-secret notification with the revision getter.
// It observes existing custody changes; it does not own authentication or retry.
export const subscribeActiveSessionRevision = (listener) => {
  sessionRevisionListeners.add(listener);
  return () => sessionRevisionListeners.delete(listener);
};

export const getActiveSessionRoles = () => (
  isDesktopRenderer() && Array.isArray(desktopRendererSession?.roles)
    ? [...desktopRendererSession.roles]
    : []
);

export const getActiveAuthToken = () => {
  if (getStoredSessionType() === SESSION_TYPE_OFFLINE) {
    if (isDesktopRenderer()) return desktopRendererSession?.token || null;
    return localStorage.getItem(OFFLINE_AUTH_TOKEN_KEY);
  }
  if (isDesktopRenderer()) return desktopRendererSession?.token || null;
  return localStorage.getItem(AUTH_TOKEN_KEY);
};

export const getAuthTokenForRequest = (url = '') => {
  if (getStoredSessionType() === SESSION_TYPE_OFFLINE) {
    return isLocalDesktopApiRequest(url)
      ? getActiveAuthToken()
      : null;
  }
  return getActiveAuthToken();
};

const captureRequestSession = (url, authorization) => ({
  revision: activeSessionRevision,
  sessionType: getStoredSessionType(),
  token: getAuthTokenForRequest(url),
  authorization,
});

const notifyInvalidSession = (url, captured) => {
  if (!isApiRequest(url) || isLoginRequest(url) || !captured?.token) return;
  // Local service admission can lag verified native custody during restart or
  // handoff. Only main or the cloud can invalidate that desktop identity.
  if (isDesktopRenderer() && isLocalDesktopApiRequest(url)) return;
  if (captured.revision !== activeSessionRevision
      || captured.sessionType !== getStoredSessionType()
      || captured.token !== getAuthTokenForRequest(url)
      || captured.authorization !== `Bearer ${captured.token}`) return;
  clearStoredSession();
  window.dispatchEvent(new CustomEvent(AUTH_INVALID_EVENT, {
    detail: { revision: activeSessionRevision, owner: activeTransitionOwner },
  }));
};

const getRequestId = (headers) => {
  if (!headers) return '';
  if (typeof headers.get === 'function') {
    return headers.get('x-request-id') || '';
  }
  return headers['x-request-id'] || headers['X-Request-ID'] || '';
};

const notifyServerError = (url, status, headers, captured) => {
  if (!isApiRequest(url) || !Number.isInteger(status) || status < 500) return;
  // Generic feedback belongs to the requesting session, including anonymous
  // login/public requests. It does not require that session's bearer authority.
  const requestUrl = captured?.url || url;
  if (!captured || captured.revision !== activeSessionRevision
      || captured.sessionType !== getStoredSessionType()
      || captured.token !== getAuthTokenForRequest(requestUrl)) return;
  const requestId = getRequestId(headers);
  if (!requestId) return;
  window.dispatchEvent(new CustomEvent(API_SERVER_ERROR_EVENT, {
    detail: { reference: requestId.slice(0, REQUEST_REFERENCE_LENGTH), revision: captured.revision },
  }));
};

// Existing screens import the shared axios package directly. Registering the
// interceptor once preserves those call sites while giving every API request
// the same authenticated-session behavior.
const axiosRequestSessions = new WeakMap();

const getAuthorization = (headers) => {
  if (typeof headers?.get === 'function') return headers.get('Authorization');
  const key = Object.keys(headers || {}).find((name) => name.toLowerCase() === 'authorization');
  return key ? headers[key] : undefined;
};

axios.interceptors.request.use((config) => {
  const url = axios.getUri(config);
  if (!isApiRequest(url)) return config;
  const token = getAuthTokenForRequest(url);
  config.headers = config.headers || {};
  if (token && getAuthorization(config.headers) === undefined) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Axios basic-auth configuration overrides Authorization during dispatch.
  axiosRequestSessions.set(config, {
    url,
    ...captureRequestSession(url, config.auth ? null : getAuthorization(config.headers)),
  });
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      const captured = axiosRequestSessions.get(error?.config);
      notifyInvalidSession(captured?.url, captured);
    }
    // Locally owned notification workflows publish their own current-session
    // errors. This opt-out affects only the generic 5xx toast, never auth custody.
    if (error?.config?.suppressServerErrorNotification !== true) {
      notifyServerError(
        error?.config?.url,
        error?.response?.status,
        error?.response?.headers,
        axiosRequestSessions.get(error?.config),
      );
    }
    return Promise.reject(error);
  },
);

export const apiFetch = async (url, options = {}) => {
  const headers = new Headers(options.headers || {});
  const token = getAuthTokenForRequest(url);
  if (token && isApiRequest(url) && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const captured = captureRequestSession(url, headers.get('Authorization'));
  const response = await fetch(url, { ...options, headers });
  if (response.status === 401) notifyInvalidSession(url, captured);
  notifyServerError(url, response.status, response.headers, captured);
  return response;
};
